import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send, Sparkles, Wine } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * SommelierChat — floating action button + drawer.
 *
 * Fase 7.1:
 *  - Label FAB rinominata "Consigliami" (era "Chiedi al Sommelier").
 *  - Montata in `PublicShell` → presente su TUTTE le pagine pubbliche
 *    (Home, /carta, /distillati, /mappa, /privacy, /account).
 *  - `onOpenWine` diventa opzionale: se assente, naviga a `/carta?wine=<id>`
 *    che apre la scheda vino nel catalogo (deep-link nativo).
 *
 * Server reply shape: `{ reply, tokens_used, remaining_rate, suggested_wines: [
 *   { id, name, producer, vintage, type }
 * ] }`.
 */
export default function SommelierChat({ onOpenWine }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId] = useState(() => `s_${Math.random().toString(36).slice(2, 10)}`);
    const [disabled, setDisabled] = useState(false);
    const [disabledReason, setDisabledReason] = useState("");
    const endRef = useRef(null);

    useEffect(() => {
        if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, open]);

    const send = async (e) => {
        e?.preventDefault?.();
        const t = text.trim();
        if (!t || busy) return;
        const nextHistory = [...messages, { role: "user", text: t }];
        setMessages(nextHistory);
        setText("");
        setBusy(true);
        try {
            // Server rejects unknown history fields, so keep only role+text.
            const history = messages.map((m) => ({ role: m.role, text: m.text }));
            const { data } = await api.post("/sommelier/chat", {
                session_id: sessionId,
                message: t,
                history,
            });
            setMessages([
                ...nextHistory,
                { role: "assistant", text: data.reply, wines: data.suggested_wines || [] },
            ]);
        } catch (err) {
            const status = err.response?.status;
            const detail = err.response?.data?.detail || "Errore";
            if (status === 429) {
                toast.error("Troppi messaggi. Riprova tra un momento.");
            } else if (status === 503) {
                setDisabled(true);
                setDisabledReason(detail);
            } else if (status === 502) {
                toast.error("Il sommelier è momentaneamente occupato. Riprova tra qualche secondo.");
            } else {
                toast.error(detail);
            }
            setMessages(nextHistory);
        } finally {
            setBusy(false);
        }
    };

    const openWine = (id) => {
        if (typeof onOpenWine === "function") {
            onOpenWine(id);
        } else {
            // Fase 7.1: navigazione fallback → deep-link Catalog che apre la scheda.
            navigate(`/carta?wine=${id}`);
        }
        // Collapse the chat on mobile so the wine card is fully visible.
        if (typeof window !== "undefined" && window.innerWidth < 640) setOpen(false);
    };

    return (
        <>
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    className="fixed bottom-6 right-6 z-40 bg-[hsl(var(--primary))] text-white rounded-full shadow-lg px-5 py-3 flex items-center gap-2 hover:shadow-xl transition-shadow"
                    data-testid="sommelier-open-btn"
                    aria-label="Apri assistente Consigliami"
                >
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm">Consigliami</span>
                </button>
            )}

            {open && (
                <div className="fixed inset-x-0 bottom-0 z-50 sm:right-6 sm:left-auto sm:bottom-6 sm:w-[380px] sm:inset-x-auto">
                    <div className="bg-white border border-[hsl(var(--border))] shadow-2xl rounded-t-lg sm:rounded-lg flex flex-col" style={{ maxHeight: "80vh" }} data-testid="sommelier-chat-panel">
                        <div className="flex items-center justify-between p-3 border-b border-[hsl(var(--border))]">
                            <div className="flex items-center gap-2">
                                <MessageCircle className="w-4 h-4 text-[hsl(var(--primary))]" strokeWidth={1.5} />
                                <div className="text-sm">Consigliami · Sommelier</div>
                            </div>
                            <button onClick={() => setOpen(false)} className="p-1" aria-label="Chiudi" data-testid="sommelier-close-btn">
                                <X className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-[280px]" data-testid="sommelier-messages">
                            {messages.length === 0 && !disabled && (
                                <div className="text-sm text-[hsl(var(--muted-foreground))] italic">
                                    Descriveti il piatto, l'occasione o le preferenze — ti aiuto a scegliere tra le referenze della carta.
                                </div>
                            )}
                            {disabled && (
                                <div className="text-sm p-3 bg-yellow-50 border border-yellow-300 rounded-sm">
                                    {disabledReason}
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                                    <div className={`max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "bg-[hsl(var(--primary))] text-white"
                                            : "bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]"
                                    }`} data-testid={`sommelier-msg-${m.role}`}>
                                        {m.text}
                                    </div>
                                    {m.role === "assistant" && m.wines && m.wines.length > 0 && (
                                        <div className="mt-2 max-w-[85%] flex flex-wrap gap-2" data-testid="sommelier-suggested-wines">
                                            {m.wines.map((w) => {
                                                const yr = w.vintage ? ` ${w.vintage}` : "";
                                                const label = `${w.name}${yr}`;
                                                return (
                                                    <button
                                                        key={w.id}
                                                        onClick={() => openWine(w.id)}
                                                        className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] rounded-full hover:bg-[hsl(var(--primary))] hover:text-white transition-colors"
                                                        title={w.producer || ""}
                                                        data-testid={`sommelier-suggested-${w.id}`}
                                                    >
                                                        <Wine className="w-3 h-3" strokeWidth={1.5} />
                                                        <span className="max-w-[180px] truncate">{label}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {busy && (
                                <div className="text-xs text-[hsl(var(--muted-foreground))] italic">Il sommelier sta scrivendo…</div>
                            )}
                            <div ref={endRef} />
                        </div>

                        <form onSubmit={send} className="p-2 border-t border-[hsl(var(--border))] flex gap-2">
                            <Input
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                disabled={busy || disabled}
                                placeholder="Chiedi un consiglio…"
                                data-testid="sommelier-chat-input"
                            />
                            <button
                                type="submit"
                                disabled={!text.trim() || busy || disabled}
                                className="co-btn-primary"
                                data-testid="sommelier-send-btn"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
